<template>
    <v-dialog v-model="dialog" max-width="800" persistent>
        <template v-slot:activator="{ on: dialogEvents, attrs }">
            <div v-if="$scopedSlots.activator" v-bind="attrs" v-on="dialogEvents">
                <slot name="activator" :on="dialogEvents" :attrs="attrs" :open="openDialog"></slot>
            </div>
            <v-btn v-else color="primary" v-bind="attrs" v-on="dialogEvents">+ user</v-btn>
        </template>

        <v-card>
            <v-card-title class="flex-nowrap align-start">
                <span>Invite Space Users</span>
                <v-spacer></v-spacer>
                <v-btn @click="closeDialog" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-text :key="inviteRenderKey">
                <v-row>
                    <v-col cols="12">
                        <p class="caption">INVITATION TYPE</p>
                        <v-row>
                            <v-col v-for="mode in invitationTypes" :key="mode.type" cols="12" sm="6">
                                <v-card outlined :class="invitationType === mode.value ? 'invite-type-card--active' : ''" @click="invitationType = mode.value">
                                    <v-card-text class="d-flex flex-row align-center pa-4">
                                        <v-icon large :color="invitationType === mode.value ? 'primary' : ''" class="mr-3">{{ mode.icon }}</v-icon>
                                        <span class="text-button" :class="invitationType === mode.value ? 'primary--text' : ''">
                                            {{ mode.type }}
                                        </span>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <template v-if="invitationType !== undefined">
                    <v-row v-if="invitationType === mainInvitationOptions.INSTANCE_USER_INVITATION && resolvedSpaceType === spaceTypes.EDUCATION_SPACE">
                        <v-col cols="12">
                            <p class="caption">INSTANCE CONFIGURATION</p>
                            <v-radio-group v-model="instanceInvitationType" class="ma-0 pa-0" hide-details>
                                <v-container fluid class="pa-0">
                                    <v-row>
                                        <v-col v-for="(instanceInvitationOption, index) in availableInstanceOptions" :key="index" cols="12" sm="6">
                                            <v-card flat :color="instanceInvitationType === instanceInvitationOption.value ? 'grey lighten-3' : ''" outlined>
                                                <v-radio :value="instanceInvitationOption.value" class="pl-4">
                                                    <template v-slot:label>
                                                        <v-card-text class="text-button">{{ instanceInvitationOption.radioLabel }}</v-card-text>
                                                    </template>
                                                </v-radio>
                                            </v-card>
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-radio-group>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <component
                                ref="activeInviteComponent"
                                v-if="displayedComponent === 'InstanceInvite'"
                                :showInviteAction="false"
                                :dataForInstanceToInviteTo="dataForInstanceInvitation"
                                :space-id="resolvedSpaceId"
                                :space-type="resolvedSpaceType"
                                @invite-action-state="syncInviteActionState"
                                @invite-success="handleInviteSuccess"
                                :is="'InstanceInvite'"></component>
                            <component
                                ref="activeInviteComponent"
                                v-else-if="displayedComponent === 'SpaceInviteFromSnapshot'"
                                :showInviteAction="false"
                                :space-id="resolvedSpaceId"
                                :space-archived="resolvedSpaceArchived"
                                @invite-action-state="syncInviteActionState"
                                @invite-success="handleInviteSuccess"
                                :is="displayedComponent"></component>
                            <component
                                ref="activeInviteComponent"
                                v-else
                                :space-id="resolvedSpaceId"
                                @invite-action-state="syncInviteActionState"
                                @invite-success="handleInviteSuccess"
                                :is="displayedComponent"></component>
                        </v-col>
                    </v-row>
                </template>
            </v-card-text>
            <v-card-actions v-if="inviteActionState.visible">
                <v-spacer></v-spacer>
                <div>
                    <v-btn v-if="inviteSuccess" color="success" icon>
                        <v-icon>mdi-check</v-icon>
                    </v-btn>
                    <v-btn v-else :loading="inviteActionState.loading" :disabled="inviteActionState.disabled" color="primary" text @click="triggerActiveInvite">
                        Invite
                    </v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import { enumsData } from '@/mixins/enums'

const InstanceInvite = () => import('@/modules/instance/components/InstanceInvite.vue')
const SpaceInviteAdmin = () => import('../components/SpaceInviteAdmin.vue')
const SpaceInviteFromSnapshot = () => import('../components/SpaceInviteFromSnapshot.vue')

const instanceInvitationsOptions = {
    INVITE_NEW_MEMBERS: 'inviteNewMembers',
    INVITE_TO_EXISTING_INSTANCE: 'inviteToExistingInstance'
}

export default {
    name: 'SpaceInviteDialog',
    components: {
        InstanceInvite,
        SpaceInviteAdmin,
        SpaceInviteFromSnapshot
    },
    mixins: [enumsData],
    props: {
        dataForInstanceToInviteTo: {
            type: Object,
            default: null
        },
        spaceId: {
            type: [Number, String],
            default: null
        },
        spaceType: {
            type: Number,
            default: null
        },
        spaceArchived: {
            type: Boolean,
            default: null
        }
    },
    data() {
        return {
            dialog: false,
            inviteRenderKey: 0,
            inviteSuccess: false,
            inviteSuccessTimeoutId: null,
            invitationType: 0,
            instanceInvitationType: instanceInvitationsOptions.INVITE_NEW_MEMBERS,
            mainInvitationOptions: {
                SPACE_ADMIN_INVITATION: 1,
                INSTANCE_USER_INVITATION: 0
            },
            invitationTypes: [
                { type: 'user invitation', value: 0, icon: 'mdi-account-multiple-outline' },
                { type: 'administrator invitation', value: 1, icon: 'mdi-shield-account-outline' }
            ],
            dataForInstanceInvitation: null,
            inviteActionState: {
                visible: true,
                loading: false,
                disabled: true
            }
        }
    },
    computed: {
        ...mapGetters('spaceStore', { routeSpaceType: 'currentSpaceType', routeSpaceArchived: 'isSpaceArchived' }),
        resolvedSpaceId() {
            return this.spaceId || this.$route.params.sid
        },
        resolvedSpaceType() {
            return this.spaceType === null ? this.routeSpaceType : this.spaceType
        },
        resolvedSpaceArchived() {
            return this.spaceArchived === null ? this.routeSpaceArchived : this.spaceArchived
        },
        displayedComponent() {
            if (this.invitationType === this.mainInvitationOptions.SPACE_ADMIN_INVITATION) {
                return 'SpaceInviteAdmin'
            }
            if (
                this.invitationType === this.mainInvitationOptions.INSTANCE_USER_INVITATION &&
                this.resolvedSpaceType === this.spaceTypes.EDUCATION_SPACE &&
                this.instanceInvitationType === instanceInvitationsOptions.INVITE_NEW_MEMBERS
            ) {
                return 'SpaceInviteFromSnapshot'
            }
            return 'InstanceInvite'
        },
        availableInstanceOptions() {
            if (this.resolvedSpaceType === this.spaceTypes.EDUCATION_SPACE) {
                return [
                    {
                        radioLabel: 'Invite to individual instances',
                        value: instanceInvitationsOptions.INVITE_NEW_MEMBERS
                    },
                    {
                        radioLabel: 'Invite to a shared instance',
                        value: instanceInvitationsOptions.INVITE_TO_EXISTING_INSTANCE
                    }
                ]
            }
            return [{ radioLabel: 'Invite to an existing instance', value: instanceInvitationsOptions.INVITE_TO_EXISTING_INSTANCE }]
        }
    },
    methods: {
        openDialog() {
            this.dialog = true
            if (this.resolvedSpaceId) {
                this.$store.commit('spaceStore/setInstances', [])
                this.$store.commit('spaceStore/setDistributedInstanceId', null)
                this.$store.commit('spaceStore/setDistributedInstance', null)
                this.$store.dispatch('spaceStore/fetchSpaceInstances', {
                    sid: this.resolvedSpaceId,
                    spaceType: this.resolvedSpaceType
                })
            }
        },
        closeDialog() {
            if (this.inviteSuccessTimeoutId) {
                clearTimeout(this.inviteSuccessTimeoutId)
                this.inviteSuccessTimeoutId = null
            }
            this.dialog = false
            this.$emit('close')
            this.inviteRenderKey += 1
            this.inviteSuccess = false
            this.inviteActionState = {
                visible: true,
                loading: false,
                disabled: true
            }
        },
        syncInviteActionState(nextState) {
            this.inviteActionState = {
                visible: Boolean(nextState?.visible),
                loading: Boolean(nextState?.loading),
                disabled: Boolean(nextState?.disabled)
            }
        },
        triggerActiveInvite() {
            this.inviteSuccess = false
            const activeComponent = this.$refs.activeInviteComponent
            if (activeComponent && activeComponent.triggerInviteFromParent) {
                activeComponent.triggerInviteFromParent()
            }
        },
        handleInviteSuccess() {
            this.inviteSuccess = true
            if (this.inviteSuccessTimeoutId) {
                clearTimeout(this.inviteSuccessTimeoutId)
            }
            this.inviteSuccessTimeoutId = setTimeout(() => {
                this.closeDialog()
            }, 2000)
        }
    },
    mounted() {
        if (this.dataForInstanceToInviteTo !== null) {
            this.instanceInvitationType = instanceInvitationsOptions.INVITE_TO_EXISTING_INSTANCE
            this.dataForInstanceInvitation = this.dataForInstanceToInviteTo
        }
    },
    watch: {
        dataForInstanceToInviteTo(nextVal, preVal) {
            if (nextVal !== preVal || (nextVal !== null && this.instanceInvitationType !== instanceInvitationsOptions.INVITE_TO_EXISTING_INSTANCE)) {
                this.instanceInvitationType = instanceInvitationsOptions.INVITE_TO_EXISTING_INSTANCE
                this.dataForInstanceInvitation = this.dataForInstanceToInviteTo
            }
        },
        displayedComponent() {
            this.inviteSuccess = false
            if (this.inviteSuccessTimeoutId) {
                clearTimeout(this.inviteSuccessTimeoutId)
                this.inviteSuccessTimeoutId = null
            }
            this.inviteActionState = {
                visible: true,
                loading: false,
                disabled: true
            }
        }
    },
    beforeDestroy() {
        if (this.inviteSuccessTimeoutId) {
            clearTimeout(this.inviteSuccessTimeoutId)
        }
    }
}
</script>

<style scoped>
.invite-type-card--active {
    border-color: var(--v-primary-base) !important;
}
</style>
